// Emite o boleto da ENTRADA do acordo de parcelamento — sozinho, logo depois que
// o termo sai — e manda para quem paga, por WhatsApp e e-mail. O resultado de cada
// etapa fica em `parcelamentos.entrada_envio`, e o card do acordo mostra o que não
// chegou (regras em ./entradaAcordoRegras.ts).
//
// O boleto só vai para o cliente depois que ele assina o termo (pedido do Vinícius
// em 16/10/2026). Antes disso ele fica emitido e guardado (`aguarda_assinatura`);
// quem manda é a varredura (efetivar-parcelamentos), que é quem vê a assinatura.
// Com o acordo já assinado, o "Emitir e enviar de novo" do card manda na hora.
//
// Nunca lança: o termo já foi enviado quando isto roda, e uma falha aqui não pode
// virar "o termo falhou". O que der errado vira etapa com `ok: false` e motivo.
//
// A emissão segue as mesmas travas da tela de emissão (BoletoEmissionModal): CPF/
// CNPJ válido, documento que já tem boleto em outro nome no Sicoob NÃO é emitido
// sozinho (o Sicoob renomearia o pagador dos boletos do outro cliente — caso AFCOM
// x ARE), e fatura que já tem boleto não é emitida de novo.

import { supabase } from './supabase';
import { registrarBoletoDaFatura } from './emitirCobrancaAvulsa';
import { boletoJaRegistrado } from './emissaoBoletoValida';
import { conflitoDePagador, documentoValido } from './pagadorBoleto';
import { enviarCobrancaEmail } from './cobrancaEmail';
import {
  contatoDaCobranca, dataEmBrasilia, descreverEnvio, entradaVencidaParaEnviar, guardarAteAssinar,
  motivoEntradaVencida, motivoSemContato, telefoneWhatsapp,
  type EntradaEnvio, type EtapaEnvio,
} from './entradaAcordoRegras.ts';

const ESTADOS_QUE_ENVIAM = new Set(['aguardando_assinatura', 'aguardando_entrada']);

const etapa = (ok: boolean, erro: string | null, extra: Partial<EtapaEnvio> = {}): EtapaEnvio =>
  ({ ok, erro, em: new Date().toISOString(), ...extra });

async function registrarNaTrilha(
  acordoId: string,
  tipo: 'entrada_enviada' | 'entrada_emitida' | 'falha',
  descricao: string,
  dados: Record<string, unknown>,
  usuario: { id?: string | null; nome?: string | null },
) {
  const { error } = await supabase.from('parcelamento_eventos').insert({
    parcelamento_id: acordoId,
    tipo,
    descricao,
    dados,
    usuario_id: usuario.id ?? null,
    usuario_nome: usuario.nome ?? null,
  });
  if (error) console.error('[entrada] falha ao gravar a trilha', error);
}

/** Chama o fluxo do WhatsApp (n8n "Fatura de Negociação") pela edge que guarda o token. */
async function enviarWhatsapp(faturaId: number, telefone: string): Promise<EtapaEnvio> {
  try {
    const { data, error } = await supabase.functions.invoke('enviar-fatura-negociacao', {
      body: { fatura_id: faturaId, telefones: [telefone] },
    });
    if (error) {
      const detalhe = await (error as any)?.context?.json?.().catch(() => null);
      return etapa(false, detalhe?.erro || detalhe?.error || (error as any)?.message || 'Falha ao chamar o envio do WhatsApp.', { destino: telefone });
    }
    if (!data?.ok) return etapa(false, data?.erro || 'O envio do WhatsApp não confirmou.', { destino: telefone });
    return etapa(true, null, { destino: telefone });
  } catch (e: any) {
    return etapa(false, e?.message || 'Falha ao chamar o envio do WhatsApp.', { destino: telefone });
  }
}

/**
 * Emite (se ainda não tem boleto) o boleto da entrada e, se o termo já foi
 * assinado, manda para quem paga. Sem assinatura, só emite e guarda.
 *
 * `soQueFalhou`: no "Tentar de novo" do card, reenvia só o canal que falhou —
 * sem isso o cliente que já recebeu pelo WhatsApp receberia de novo.
 */
export async function emitirEEnviarEntrada(
  acordoId: string,
  usuario: { id?: string | null; nome?: string | null },
  opcoes: { soQueFalhou?: boolean } = {},
): Promise<EntradaEnvio> {
  const agora = new Date().toISOString();
  const pulado = (motivo: string): EntradaEnvio => ({ em: agora, pulado: motivo });

  const { data: acordo, error: erroAcordo } = await supabase
    .from('parcelamentos')
    .select('id, status, entrada_fatura_id, termo_dados, entrada_envio')
    .eq('id', acordoId)
    .maybeSingle();
  if (erroAcordo || !acordo) return pulado(`Não consegui ler o acordo: ${erroAcordo?.message || 'não encontrado'}.`);
  if (!ESTADOS_QUE_ENVIAM.has(acordo.status)) return pulado('O acordo não está aguardando assinatura nem entrada.');
  if (!acordo.entrada_fatura_id) return pulado('O acordo não tem fatura de entrada.');

  const anterior = (acordo.entrada_envio || null) as EntradaEnvio | null;
  const gravar = async (resultado: EntradaEnvio) => {
    const { error } = await supabase.from('parcelamentos').update({ entrada_envio: resultado }).eq('id', acordoId);
    if (error) console.error('[entrada] falha ao gravar entrada_envio', error);
    return resultado;
  };

  // Lida do BANCO agora, nunca de um estado de tela: é o que impede emitir duas vezes.
  const { data: fatura, error: erroFatura } = await supabase
    .from('faturas')
    .select('*')
    .eq('id', acordo.entrada_fatura_id)
    .maybeSingle();
  if (erroFatura || !fatura) {
    return gravar({ em: agora, emissao: etapa(false, `Não consegui ler a fatura da entrada: ${erroFatura?.message || 'não encontrada'}.`) });
  }
  const statusBoleto = String(fatura.status_boleto_vigor || '').toLowerCase();
  if (statusBoleto === 'pago' || statusBoleto.startsWith('cancelad')) return pulado(`A entrada está "${fatura.status_boleto_vigor}".`);

  // Boleto de empresa parceira (Gran) sai pelo Asaas dela, e o PDF da negociação
  // leva a Associação Vigor como beneficiária — mandar isso seria errado. Fica o
  // aviso para o analista emitir pela tela de Faturas.
  if (fatura.empresa_parceira_id) {
    return gravar(pulado('Boleto de empresa parceira (Gran): emita e envie pela tela de Faturas.'));
  }

  // --- 1. Emissão -------------------------------------------------------------
  let emissao: EtapaEnvio;
  if (boletoJaRegistrado(fatura)) {
    emissao = etapa(true, null, { boleto: fatura.num_cobranca_boleto });
  } else if (!documentoValido(fatura.cpf_cnpj_cliente_boleto)) {
    emissao = etapa(false, `CPF/CNPJ de quem paga está vazio ou inválido (${fatura.cpf_cnpj_cliente_boleto || 'vazio'}).`);
  } else if (!(Number(fatura.valor_boleto_vigor) > 0) || !fatura.vencimento_boleto) {
    emissao = etapa(false, 'A entrada está sem valor ou sem vencimento.');
  } else {
    // Boleto de teste não passa pelo Sicoob (emitir-boleto-teste só preenche dados
    // fictícios), então não há pagador para renomear lá.
    const conflito = fatura.ambiente === 'sandbox'
      ? null
      : await conflitoDePagador(fatura.cpf_cnpj_cliente_boleto, fatura.nome_cliente_boleto, fatura.id);
    if (conflito) {
      emissao = etapa(false,
        `O CPF/CNPJ ${fatura.cpf_cnpj_cliente_boleto} já tem boleto em nome de "${conflito.nomeRegistrado}". `
        + 'Emitir sozinho renomearia o pagador no Sicoob — emita pela tela de Faturas, que pede a confirmação.');
    } else {
      try {
        const emitida = await registrarBoletoDaFatura(fatura, {
          ucCodigo: fatura.uc_codigo,
          nomeQuem: usuario.nome || 'Sistema (acordo)',
          sandbox: fatura.ambiente === 'sandbox',
        });
        emissao = etapa(true, null, { boleto: emitida?.num_cobranca_boleto ?? null });
      } catch (e: any) {
        emissao = etapa(false, e?.message || 'A emissão falhou sem dizer o motivo.');
      }
    }
  }

  if (!emissao.ok) {
    const resultado = await gravar({ em: agora, emissao });
    await registrarNaTrilha(acordoId, 'falha', `Boleto da entrada NÃO foi emitido: ${emissao.erro}`, { entrada_envio: resultado }, usuario);
    return resultado;
  }

  // --- 2. Envio ---------------------------------------------------------------
  // Sem assinatura o boleto fica guardado: a varredura manda quando o cliente assinar.
  if (acordo.status === 'aguardando_assinatura') {
    const resultado = await gravar(guardarAteAssinar(agora, emissao, anterior));
    await registrarNaTrilha(
      acordoId, 'entrada_emitida',
      `Boleto da entrada${emissao.boleto ? ` ${emissao.boleto}` : ''} emitido. Ele vai para o cliente quando o termo for assinado.`,
      { entrada_envio: resultado }, usuario,
    );
    return resultado;
  }

  // Assinado, mas a entrada venceu (ou vence hoje): esse boleto não serve mais.
  // Mandar só depois de emitir com data nova pela tela de Faturas.
  if (entradaVencidaParaEnviar(fatura.vencimento_boleto, dataEmBrasilia())) {
    const vencida = String(fatura.vencimento_boleto).slice(0, 10);
    const resultado = await gravar({ em: agora, emissao, vencida });
    await registrarNaTrilha(acordoId, 'falha', motivoEntradaVencida(vencida), { entrada_envio: resultado }, usuario);
    return resultado;
  }

  const contato = contatoDaCobranca(acordo.termo_dados);
  const pular = (canal: 'whatsapp' | 'email') => !!opcoes.soQueFalhou && !!anterior?.[canal]?.ok;

  let whatsapp: EtapaEnvio;
  if (pular('whatsapp')) {
    whatsapp = anterior!.whatsapp!;
  } else {
    const semContato = motivoSemContato(contato, 'whatsapp');
    whatsapp = semContato
      ? etapa(false, semContato)
      : await enviarWhatsapp(fatura.id, telefoneWhatsapp(contato?.whatsapp)!);
  }

  let email: EtapaEnvio;
  if (pular('email')) {
    email = anterior!.email!;
  } else if (motivoSemContato(contato, 'email')) {
    email = etapa(false, motivoSemContato(contato, 'email'));
  } else {
    const r = await enviarCobrancaEmail(fatura.id, { destinatarios: [contato.email], forcar: !!opcoes.soQueFalhou });
    email = etapa(r.ok, r.ok ? null : (r.mensagem || 'O e-mail não saiu.'), { destino: contato.email });
  }

  const resultado = await gravar({ em: agora, emissao, whatsapp, email });
  const { tipo, descricao } = descreverEnvio(resultado, contato?.nome);
  await registrarNaTrilha(acordoId, tipo, descricao, { entrada_envio: resultado }, usuario);
  return resultado;
}
